import { useEffect, useState } from 'react';
import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import { useTranslation } from '@hooks/useTranslation';
import styles from './RequestActivationModal.module.scss';

export type ActivationMode = 'send_for_review' | 'skip_review';

interface RequestActivationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (mode: ActivationMode) => void;
  isSubmitting: boolean;
}

export function RequestActivationModal({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
}: RequestActivationModalProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ActivationMode | null>(null);

  // Force the user to make an explicit choice each time the modal opens.
  useEffect(() => {
    if (isOpen) setMode(null);
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('K_MODAL_TITLE_REQUEST_ACTIVATION', 'Request Activation')}
      size="sm"
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button
            variant="filled"
            onClick={() => mode && onConfirm(mode)}
            disabled={!mode}
            loading={isSubmitting}
          >
            {t('K_BUTTON_PROCEED', 'Proceed')}
          </Button>
        </>
      }
    >
      <p className={styles.modalIntro}>
        {t(
          'K_MODAL_REQUEST_ACTIVATION_INTRO',
          'Choose how you would like to proceed with your activation.',
        )}
      </p>

      <div className={styles.optionList}>
        <label
          className={`${styles.option} ${mode === 'send_for_review' ? styles.optionSelected : ''}`}
        >
          <input
            type="radio"
            name="activationMode"
            value="send_for_review"
            checked={mode === 'send_for_review'}
            onChange={() => setMode('send_for_review')}
            disabled={isSubmitting}
          />
          <div>
            <div className={styles.optionTitle}>
              {t('K_OPTION_SEND_FOR_REVIEW_TITLE', 'Review contracts')}
            </div>
            <div className={styles.optionDesc}>
              {t(
                'K_OPTION_SEND_FOR_REVIEW_DESC',
                'Your account manager will review your contract templates before activation.',
              )}
            </div>
          </div>
        </label>

        <label
          className={`${styles.option} ${mode === 'skip_review' ? styles.optionSelected : ''}`}
        >
          <input
            type="radio"
            name="activationMode"
            value="skip_review"
            checked={mode === 'skip_review'}
            onChange={() => setMode('skip_review')}
            disabled={isSubmitting}
          />
          <div>
            <div className={styles.optionTitle}>
              {t('K_OPTION_SKIP_REVIEW_TITLE', 'Skip contract review')}
            </div>
            <div className={styles.optionDesc}>
              {t(
                'K_OPTION_SKIP_REVIEW_DESC',
                'Proceed directly to activation pending. Contract templates can still be reviewed later.',
              )}
            </div>
          </div>
        </label>
      </div>
    </Modal>
  );
}

export default RequestActivationModal;
